import { motion } from "framer-motion";
import { Flame, ShieldCheck, BadgeCheck, ArrowRight, Check } from "lucide-react";
import { Reveal, SectionLabel } from "@/lib/Reveal";
import { BUSINESS } from "@/lib/data";

const INCLUDED = [
  "Old boiler removed & disposed of",
  "New boiler supplied & professionally fitted",
  "Full system flush & magnetic filter",
  "Gas Safe registration & certificate",
  "Manufacturer warranty registration",
  "Heating controls set up & explained",
];

const BOILER_IMG = "/images/work-boiler.webp";

export const NewBoiler = () => {
  return (
    <section id="new-boilers" data-testid="new-boilers-section" className="py-20 lg:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal className="relative hidden lg:block order-1">
          <div className="absolute -inset-3 border border-torch/40 translate-x-5 translate-y-5 pointer-events-none" />
          <div className="relative overflow-hidden rounded-sm">
            <img
              src={BOILER_IMG}
              alt="New boiler installation by Teign Gas"
              loading="lazy"
              className="w-full h-[520px] object-cover grayscale-[30%] contrast-125"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 border border-white/15 bg-black/60 backdrop-blur-md px-5 py-4 flex items-center justify-between">
              <p className="font-mono text-[10px] tracking-[0.25em] text-neutral-300 uppercase">
                Worcester Bosch installers
              </p>
              <Flame className="w-4 h-4 text-torch" />
            </div>
          </div>
        </Reveal>

        <div className="order-2">
          <Reveal><SectionLabel>02 / New boilers</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white mt-4">
              New boiler from<br /><span className="text-torch">£1,799 fitted.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-neutral-400 leading-relaxed max-w-md">
              Old boiler on its last legs? We supply and fit A-rated boilers across
              Teignbridge — properly sized for your home, flushed, commissioned and
              registered, all by Gas Safe engineers. One fixed quote, no surprises.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 border border-torch/30 bg-torch/5 rounded-sm p-6" data-testid="new-boiler-price-card">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="font-mono text-[10px] tracking-[0.25em] text-torch uppercase">Fully fitted from</span>
                <span className="font-mono font-bold text-4xl text-white">£1,799</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                <span className="flex items-center gap-2 font-mono text-[10px] tracking-wider text-neutral-400 uppercase">
                  <ShieldCheck className="w-3.5 h-3.5 text-torch" /> Gas Safe registered
                </span>
                <span className="flex items-center gap-2 font-mono text-[10px] tracking-wider text-neutral-400 uppercase">
                  <BadgeCheck className="w-3.5 h-3.5 text-torch" /> Up to 10-yr warranty
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3.5" data-testid="new-boiler-included-list">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-neutral-300">
                  <Check className="w-4 h-4 text-torch shrink-0 mt-0.5" strokeWidth={3} />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#quote"
                data-testid="new-boiler-quote-btn"
                className="group bg-torch hover:bg-torch-hover text-white font-display font-bold uppercase tracking-wide px-7 py-3.5 rounded-sm flex items-center gap-3 transition-all hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
              >
                Get my free boiler quote
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={BUSINESS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="new-boiler-whatsapp-btn"
                className="border border-neutral-700 hover:border-torch text-neutral-200 hover:text-torch font-display font-bold uppercase tracking-wide px-7 py-3.5 rounded-sm transition-colors"
              >
                WhatsApp us your boiler
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
